import type { ActionTree, ActionContext, Module, MutationTree, Commit } from 'vuex';
import { type AxiosResponse } from 'axios';
import { StorageConstant, ProspectProfileModuleConstant, CookieConstant } from '../../constants';
import { nearestClubService } from '../../services/nearestClubService';
import { storageService } from '@life-time-inc/enterprise-framework-core';
import { sessionService } from '@life-time-inc/enterprise-framework-core';
import type { ltFramework } from '@life-time-inc/enterprise-framework-core';

interface IProspectProfileState {
	prospectProfile: ltFramework.IProspectProfile;
}

const ProspectProfileState: IProspectProfileState = {
	prospectProfile: {
		club: {
			isClubPage: false
		},
		initialized: false
	} as ltFramework.IProspectProfile
};

const { action, mutation } = ProspectProfileModuleConstant;

const ProspectProfileMutations: MutationTree<IProspectProfileState> = {
	[mutation.setProspectProfile](
		state: IProspectProfileState,
		payload: ltFramework.IProspectProfile
	) {
		state.prospectProfile = payload;
	}
};

const ProspectProfileActions: ActionTree<IProspectProfileState, ltFramework.IRootState> = {
	[action.initialize]({
		commit,
		dispatch
	}: ActionContext<IProspectProfileState, ltFramework.IRootState>): Promise<void> {
		const clubDetails: ltFramework.IClubDetailsFromDOM =
			window.lt?.club || ({} as ltFramework.IClubDetailsFromDOM);
		const pageProperties: ltFramework.IPageProperties =
			window.lt?.pageProperties || ({} as ltFramework.IPageProperties);
		const isClubPage = pageProperties?.club;
		const storedClubData: ltFramework.IProspectClubData = storageService.localGet(
			StorageConstant.clubData
		) as ltFramework.IProspectClubData;

		if (storedClubData && !clubDetails.mmsClubId) {
			// the club is in localStorage, but not stored on window.lt.club
			// check if the storedClubData is expired
			const currentTime = new Date().getTime();
			const storedTime = storedClubData.timestamp;
			const sixHourTimeout = 6 * 60 * 60 * 1000;
			if (!storedTime || currentTime - storedTime >= sixHourTimeout) {
				clubDetails.mmsClubId = storedClubData.id; // stored club data is expired
			}
		}

		// Choose how to populate prospectProfile.club

		if (clubDetails && clubDetails.mmsClubId) {
			// Get nearest club to this club id
			return dispatch(action.updateProspectClub, clubDetails.mmsClubId);
		} else if (storedClubData) {
			// Use the storedClubData
			const prospectProfile: ltFramework.IProspectProfile = {
				club: storedClubData,
				initialized: true
			};

			commit(mutation.setProspectProfile, prospectProfile);

			return Promise.resolve();
		} else if (!pageProperties.disableProspectGeoLocation) {
			// Get nearest club by geolocation
			return nearestClubService
				.getNearestClub()
				.then((clubResponse: AxiosResponse<ltFramework.IClubFromService[]>) => {
					commitClubData(commit, clubResponse.data[0], isClubPage);
				});
		} else {
			return Promise.resolve();
		}
	},

	[action.updateProspectClub](
		{ commit }: ActionContext<IProspectProfileState, ltFramework.IRootState>,
		clubId: number
	): Promise<void> {
		return nearestClubService
			.getNearestClubs({
				cId: clubId,
				limit: 1,
				distance: 9999,
				latitude: null,
				longitude: null
			})
			.then((clubResponse: AxiosResponse<ltFramework.IClubFromService[]>) => {
				const pageProperties: ltFramework.IPageProperties =
					window.lt?.pageProperties || ({} as ltFramework.IPageProperties);
				const isClubPage = pageProperties?.club;

				commitClubData(commit, clubResponse.data[0], isClubPage);
			});
	}
};

export const ProspectProfileModule: Module<IProspectProfileState, ltFramework.IRootState> = {
	namespaced: true,
	state: ProspectProfileState,
	actions: ProspectProfileActions,
	mutations: ProspectProfileMutations
};

/**
 * Utility function to save prospectProfile club data to localStorage and commit it to Vuex
 *
 * @param commit - reference to Vuex commit function
 * @param clubDetails - club (either from DOM or service)
 * @param isClubPage - whether `window.lt.pageProperties.club` is `true`
 */
function commitClubData(
	commit: Commit,
	clubDetails: ltFramework.IClubDetailsFromDOM | ltFramework.IClubFromService,
	isClubPage: boolean
) {
	const pages = { ...clubDetails.clubPagePaths };

	const club: ltFramework.IProspectClubData = {
		country: clubDetails.country,
		id: clubDetails.mmsClubId,
		clubTypeId: clubDetails.clubTypeId,
		domainPrefix: clubDetails.domainPrefix,
		street: clubDetails.street1,
		city: clubDetails.city,
		state: clubDetails.stateAbbr,
		stateName: clubDetails.state,
		zip: clubDetails.zip,
		region: clubDetails.region,
		area: clubDetails.area,
		timeZone: clubDetails.timeZone,
		membershipLevel: clubDetails.membershipLevel,
		status: clubDetails.open,
		openDate: clubDetails.openDate,
		latitude: clubDetails.latitude,
		longitude: clubDetails.longitude,
		// on the next line `in` is used to distinguish between input types so TS doesn't complain
		name: 'name' in clubDetails ? clubDetails.name : clubDetails.marketingClubName,
		description: clubDetails.comingSoon || undefined,
		phone: clubDetails.phone,
		link: clubDetails.clubPagePath || window.location.href,
		prospectPagePath: clubDetails.prospectPagePath || window.location.href,
		juniorAccess: clubDetails.juniorAccess,
		pages,
		validPageKeys: Object.keys(pages),
		isClubPage,
		timestamp: new Date().getTime()
	};

	storageService.localSet(StorageConstant.clubData, club);

	sessionService.setCookie(CookieConstant.lastVisitedLocation, club.id.toString(), Infinity);

	const prospectProfile: ltFramework.IProspectProfile = {
		club,
		initialized: true
	};

	commit(mutation.setProspectProfile, prospectProfile);
}
